import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, Download } from "lucide-react";
import { api, API } from "@/lib/quotationApi";
import MasterItemRow from "@/components/admin/MasterItemRow";

export default function MasterList({ endpoint, importExportKey, priceKey, priceLabel, title, kind }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [editingId, setEditingId] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(endpoint);
      setItems(r.data);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onAdd = async () => {
    if (!newName.trim() || !newPrice) return;
    try {
      await api.post(endpoint, {
        name: newName.trim(),
        [priceKey]: parseFloat(newPrice),
      });
      setNewName("");
      setNewPrice("");
      setAdding(false);
      toast.success(`${title} added`);
      load();
    } catch {
      toast.error("Add failed");
    }
  };

  const onSaveEdit = async (id, body) => {
    try {
      await api.put(`${endpoint}/${id}`, body);
      setEditingId(null);
      toast.success("Updated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(`Delete this ${kind}?`)) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const onExportCSV = () => {
    const url = `${API}/admin/export/${importExportKey}`;
    window.open(url, "_blank");
  };

  const onImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await api.post(
        `/admin/import/${importExportKey}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`Imported: ${r.data.added} added, ${r.data.updated} updated`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div>
      {/* CSV actions */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          onClick={onExportCSV}
          className="btn-ghost !h-11 text-sm flex items-center justify-center gap-2"
          data-testid={`export-${kind}-btn`}
        >
          <Download size={14} />
          Export CSV
        </button>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="btn-ghost !h-11 text-sm flex items-center justify-center gap-2"
          data-testid={`import-${kind}-btn`}
        >
          <Upload size={14} />
          Import CSV
        </button>
        <input
          type="file"
          ref={fileInputRef}
          accept=".csv,text/csv"
          className="hidden"
          onChange={onImportCSV}
        />
      </div>

      {!adding && (
        <button
          onClick={() => setAdding(true)}
          className="btn-ghost w-full flex items-center justify-center gap-2 mb-4"
          data-testid={`add-${kind}-btn`}
        >
          <Plus size={16} />
          Add {title}
        </button>
      )}

      {adding && (
        <div className="card-luxe mb-4">
          <div className="overline text-[#C5A880] mb-3">New {title}</div>
          <label className="label-luxe">Name</label>
          <input
            className="input-luxe"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={`e.g. ${kind === "finish" ? "Premium Acrylic" : "Soft-close Hinge"}`}
            data-testid={`new-${kind}-name`}
          />
          <label className="label-luxe mt-4">{priceLabel}</label>
          <input
            type="number"
            className="input-luxe"
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
            inputMode="numeric"
            data-testid={`new-${kind}-price`}
          />
          <div className="flex gap-2 mt-4">
            <button className="btn-ghost flex-1" onClick={() => setAdding(false)}>
              Cancel
            </button>
            <button
              onClick={onAdd}
              disabled={!newName.trim() || !newPrice}
              className="btn-primary flex-1"
              data-testid={`save-${kind}-btn`}
            >
              Save
            </button>
          </div>
        </div>
      )}

      <div className="card-luxe !p-2">
        {loading && <div className="p-4 text-white/40 text-sm">Loading...</div>}
        {!loading && items.length === 0 && (
          <div className="p-6 text-center text-white/40 text-sm">
            No items yet. Tap &quot;Add {title}&quot; above.
          </div>
        )}
        {items.map((item) => (
          <MasterItemRow
            key={item.id}
            item={item}
            kind={kind}
            endpoint={endpoint}
            priceKey={priceKey}
            priceLabel={priceLabel}
            isEditing={editingId === item.id}
            onStartEdit={() => setEditingId(item.id)}
            onCancelEdit={() => setEditingId(null)}
            onSave={(body) => onSaveEdit(item.id, body)}
            onDelete={() => onDelete(item.id)}
            onReload={load}
          />
        ))}
      </div>

      <div className="text-[10px] text-white/30 mt-3 text-center">
        CSV format: <code className="text-white/50">name,{priceKey}</code>
      </div>
    </div>
  );
}
